// src/pages/Session.js
import { useParams } from "react-router";
import "./Session.scss";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Analytics from "../services/APIDeprecated";
import Header from "../components/header/Header";
import { getThumbnail } from "../services/Content";
import { useContent } from "../context/ContentContext";
import ContactLink from "../components/header/ContactLink";
import SessionCodeInput from "../components/header/SessionCodeInput";
import PricingLink from "../components/header/PricingLink";
import Score from "../components/dashboard/Score";
import AnalyticsHeader from "../components/AnalyticsHeader";
import { usePageMetadata } from "../context/PageMetadataContext";
import { useExperiences } from "../context/ExperiencesContext";
import gradientColor from "../utility/gradientColor";

// Import placeholder images
import Placeholder1 from "../assets/env-placeholder-1.png";
import Placeholder2 from "../assets/env-placeholder-2.png";
import Placeholder3 from "../assets/env-placeholder-3.png";
import Placeholder4 from "../assets/env-placeholder-4.png";
import Placeholder5 from "../assets/env-placeholder-5.png";
import Placeholder6 from "../assets/env-placeholder-6.png";
import Placeholder7 from "../assets/env-placeholder-7.png";

const PLACEHOLDERS = [
  Placeholder1,
  Placeholder2,
  Placeholder3,
  Placeholder4,
  Placeholder5,
  Placeholder6,
  Placeholder7,
];

function getPlaceholder(id) {
  if (!id) return PLACEHOLDERS[0];
  const hash = id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return PLACEHOLDERS[hash % PLACEHOLDERS.length];
}

const POLL_MS = 3000;
const LIVE_ACTIVITY_WINDOW_MS = 5 * 60 * 1000;

function normalizeSessionKey(raw) {
  const s = (raw || "").toString().trim();
  if (!s) return "";
  return s.toLowerCase();
}

function extractSessionEvents(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return null;
}

function countByLabel(data, label) {
  if (!Array.isArray(data)) return 0;
  return data.filter((d) => d?.label === label).length;
}

function countAssessments(data) {
  if (!Array.isArray(data)) return 0;
  return data.filter((d) => (d?.pk || "").includes("Assessment")).length;
}

function safeDate(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function formatClock(ts) {
  const d = safeDate(ts);
  if (!d) return "—";
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDateRange(start, end) {
  if (!start && !end) return "—";
  const opts = { month: "long", day: "numeric", year: "numeric" };
  const tOpts = { hour: "numeric", minute: "2-digit", hour12: true };
  const s = start
    ? `${start.toLocaleDateString(undefined, opts)} ${start.toLocaleTimeString(undefined, tOpts)}`
    : "—";
  const e = end
    ? `${end.toLocaleDateString(undefined, opts)} ${end.toLocaleTimeString(undefined, tOpts)}`
    : "—";
  return `${s} – ${e}`;
}

function secondsBetween(a, b) {
  if (!a || !b) return null;
  const ms = b.getTime() - a.getTime();
  if (!Number.isFinite(ms) || ms <= 0) return null;
  return Math.round(ms / 1000);
}

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return "—";
  const mins = Math.round(seconds / 60);
  return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
}


function splitTopics(t) {
  if (!t) return [];
  if (Array.isArray(t)) return t.map(String).filter(Boolean);
  return String(t)
    .split(/[,|]/g)
    .map((s) => s.trim())
    .filter(Boolean);
}

function safeString(v) {
  if (v == null) return "";
  return typeof v === "string" ? v : String(v);
}

function firstTextValue(...values) {
  for (const value of values) {
    const text = safeString(value).trim();
    if (text) return text;
  }
  return "";
}

function safeJsonParse(v) {
  try {
    if (v == null) return null;
    if (typeof v === "object") return v;
    return JSON.parse(String(v));
  } catch {
    return null;
  }
}

function isDarkThemeActive() {
  if (typeof document === "undefined") return false;

  const themeAttribute = document.documentElement?.getAttribute("data-theme");
  if (themeAttribute === "dark") return true;
  if (themeAttribute === "light") return false;

  if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  }

  return false;
}

function getScoreSurfaceStyle(score) {
  const numericScore = Number(score);
  if (!Number.isFinite(numericScore)) {
    return { background: "var(--color-bg-highlight)" };
  }

  return { background: gradientColor(numericScore, { darkMode: isDarkThemeActive() }) };
}

function getStickyHeaderOffset() {
  if (typeof document === "undefined") return 80;

  const rawValue = window
    .getComputedStyle(document.documentElement)
    .getPropertyValue("--layout-header-height")
    .trim();
  const parsedValue = Number.parseFloat(rawValue);

  return Number.isFinite(parsedValue) ? parsedValue : 80;
}

function serializeComparableValue(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;

  try {
    return JSON.stringify(value);
  } catch {
    return safeString(value);
  }
}

function getEventType(pk) {
  const s = safeString(pk);
  if (!s) return "";
  return s.split("#")[0] || "";
}

function shouldRenderInTimeline(ev) {
  const type = getEventType(ev?.pk);
  return type === "EnvironmentVisited" || type === "ElementExpanded" || type === "Assessment";
}

function findFirstTextField(events, fieldNames) {
  if (!Array.isArray(events) || !Array.isArray(fieldNames)) return "";

  for (const event of events) {
    const parsedValue = safeJsonParse(event?.value);
    const candidates = [event, event?.metadata, parsedValue];

    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== "object") continue;

      for (const fieldName of fieldNames) {
        const text = firstTextValue(candidate?.[fieldName]);
        if (text) return text;
      }
    }
  }

  return "";
}

function getNamedItemLabel(item) {
  return firstTextValue(
    item?.name,
    item?.environmentName,
    item?.title,
    item?.metadata?.name
  );
}

function getEnvironmentEventDetails(event) {
  const parsedValue = safeJsonParse(event?.value);

  const environmentId = firstTextValue(
    event?.environmentId,
    event?.environmentID,
    parsedValue?.environmentId,
    parsedValue?.environmentID,
    parsedValue?.id,
    typeof event?.value === "string" ? event.value : ""
  );

  const environmentName = firstTextValue(
    event?.environmentName,
    parsedValue?.environmentName,
    parsedValue?.name,
    parsedValue?.title
  );

  return { environmentId, environmentName };
}

function getElementEventId(event) {
  const parsedValue = safeJsonParse(event?.value);

  return firstTextValue(
    event?.elementId,
    event?.elementID,
    parsedValue?.elementId,
    parsedValue?.elementID,
    parsedValue?.id,
    typeof event?.value === "string" ? event.value : ""
  );
}

function areEventsEquivalent(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;

  return (
    safeString(a?.pk) === safeString(b?.pk) &&
    safeString(a?.sk) === safeString(b?.sk) &&
    safeString(a?.label) === safeString(b?.label) &&
    safeString(a?.environmentId) === safeString(b?.environmentId) &&
    safeString(a?.environmentName) === safeString(b?.environmentName) &&
    safeString(a?.elementId) === safeString(b?.elementId) &&
    safeString(a?.elementID) === safeString(b?.elementID) &&
    safeString(a?.transcript) === safeString(b?.transcript) &&
    safeString(a?.student_evaluation) === safeString(b?.student_evaluation) &&
    safeString(a?.summary) === safeString(b?.summary) &&
    safeString(a?.score) === safeString(b?.score) &&
    safeString(a?.topics) === safeString(b?.topics) &&
    serializeComparableValue(a?.value) === serializeComparableValue(b?.value)
  );
}

function areEventListsEquivalent(prev, next) {
  if (prev === next) return true;
  if (!Array.isArray(prev) || !Array.isArray(next)) return false;
  if (prev.length !== next.length) return false;

  for (let index = 0; index < prev.length; index += 1) {
    if (!areEventsEquivalent(prev[index], next[index])) {
      return false;
    }
  }

  return true;
}

export default function Session({ dashboard }) {
  const { sessionId: rawSessionId } = useParams();
  const sessionKey = useMemo(() => normalizeSessionKey(rawSessionId), [rawSessionId]);
  const { getItem, mySystems } = useContent();
  const { experiences } = useExperiences();

  const [sessionData, setSessionData] = useState(null);
  const [experience, setExperience] = useState("Unknown Experience");
  const [isPolling, setIsPolling] = useState(false);

  const [environmentsVisited, setEnvironmentsVisited] = useState(0);
  const [descriptionsRead, setDescriptionsRead] = useState(0);
  const [insightsCompleted, setInsightsCompleted] = useState(0);

  const [notFound, setNotFound] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const { setTitle, setShowTitleInHeader, setHeaderAction } = usePageMetadata();
  const cancelledRef = useRef(false);
  const kpiSectionRef = useRef(null);
  const stickyMeasureFrameRef = useRef(0);
  const manualPollingOverrideRef = useRef({ sessionKey: "", enabled: false });
  const [showStickyKpis, setShowStickyKpis] = useState(false);

  useEffect(() => {
    setShowTitleInHeader(true);
    setTitle("Session");
    return () => {
      cancelledRef.current = true;
    };
  }, [setShowTitleInHeader, setTitle]);

  useEffect(() => {
    if (experience) setTitle(`Session - ${experience}`);
  }, [experience, setTitle]);

  useEffect(() => {
    manualPollingOverrideRef.current = { sessionKey, enabled: false };
    setIsPolling(false);
    setSessionData(null);
  }, [sessionKey]);

  const computeStats = (data) => {
    setEnvironmentsVisited(countByLabel(data, "EnvironmentVisited"));
    setDescriptionsRead(countByLabel(data, "ElementExpanded"));
    setInsightsCompleted(countAssessments(data));
  };

  const applySessionData = useCallback((nextData) => {
    setSessionData((prevData) => (areEventListsEquivalent(prevData, nextData) ? prevData : nextData));
    computeStats(nextData);
  }, []);

  const handlePollingToggle = useCallback(
    (updater) => {
      manualPollingOverrideRef.current = { sessionKey, enabled: true };
      setIsPolling((previous) =>
        typeof updater === "function" ? updater(previous) : Boolean(updater)
      );
    },
    [sessionKey]
  );

  useEffect(() => {
    return () => setHeaderAction(null);
  }, [setHeaderAction]);

  const resolveExperienceName = useCallback(
    async (data) => {
      try {
        const experienceId = findFirstTextField(data, ["experienceId"]);
        const systemId = findFirstTextField(data, ["systemId", "systemID"]);

        const matchedExperience = Array.isArray(experiences)
          ? experiences.find((item) =>
              [item?.experienceId, item?.id].filter(Boolean).includes(experienceId)
            )
          : null;
        const experienceName = getNamedItemLabel(matchedExperience);
        if (experienceName) {
          if (!cancelledRef.current) setExperience(experienceName);
          return;
        }

        const matchedSystem = Array.isArray(mySystems)
          ? mySystems.find((item) =>
              [item?.id, item?.systemId].filter(Boolean).includes(systemId)
            )
          : null;
        const systemName = getNamedItemLabel(matchedSystem);
        if (systemName) {
          if (!cancelledRef.current) setExperience(systemName);
          return;
        }

        if (!systemId) {
          if (!cancelledRef.current) setExperience("Unknown Experience");
          return;
        }

        const res = await getItem("systems", systemId);
        const name = getNamedItemLabel(res) || "Unknown Experience";
        if (!cancelledRef.current) setExperience(name);
      } catch {
        if (!cancelledRef.current) setExperience("Unknown Experience");
      }
    },
    [experiences, getItem, mySystems]
  );

  const fetchSession = async () => {
    if (!sessionKey) {
      setSessionData(null);
      setNotFound(true);
      setFetchError(null);
      computeStats(null);
      setExperience("Unknown Experience");
      return;
    }

    try {
      setFetchError(null);
      setNotFound(false);

      const lookups = [
        dashboard
          ? {
              endpoint: `sessions/${encodeURIComponent(sessionKey)}`,
              config: { requiresAuth: true },
              allowFallback: true,
            }
          : null,
        {
          endpoint: `sessioncode/${encodeURIComponent(sessionKey)}`,
          config: undefined,
          allowFallback: false,
        },
        {
          endpoint: `usercode/${encodeURIComponent(sessionKey)}`,
          config: undefined,
          allowFallback: false,
        },
      ].filter(Boolean);

      for (const lookup of lookups) {
        try {
          const res = await Analytics.get(lookup.endpoint, lookup.config);
          const data = extractSessionEvents(res?.data);

          if (!Array.isArray(data)) {
            throw Object.assign(new Error(`Empty ${lookup.endpoint} response`), { _empty: true });
          }
          if (cancelledRef.current) return;

          applySessionData(data);
          return;
        } catch (lookupError) {
          const status = lookupError?.response?.status;
          const isEmpty = lookupError?._empty === true;
          const isNotFound = status === 404;

          if (!lookup.allowFallback && !isNotFound && !isEmpty) {
            throw lookupError;
          }

          if (lookup.allowFallback && !isNotFound && !isEmpty) {
            continue;
          }
        }
      }

      if (cancelledRef.current) return;

      setSessionData(null);
      setNotFound(true);
      computeStats(null);
      setExperience("Unknown Experience");
    } catch (e) {
      if (cancelledRef.current) return;

      const status = e?.response?.status;
      if (status === 404) {
        setSessionData(null);
        setNotFound(true);
        setFetchError(null);
        computeStats(null);
        setExperience("Unknown Experience");
        return;
      }

      setSessionData(null);
      setNotFound(false);
      setFetchError(e);
      computeStats(null);
      setExperience("Unknown Experience");
    }
  };

  useEffect(() => {
    resolveExperienceName(sessionData);
  }, [resolveExperienceName, sessionData]);

  useEffect(() => {
    cancelledRef.current = false;

    let intervalId = null;
    fetchSession();

    if (isPolling) intervalId = setInterval(fetchSession, POLL_MS);

    return () => {
      cancelledRef.current = true;
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey, isPolling]);

  const hasData = Array.isArray(sessionData) && sessionData.length > 0;
  const rawDisplay = (rawSessionId || "").toString().trim();

  const { startAt, endAt, rawDurationLabel } = useMemo(() => {
    if (!hasData) return { startAt: null, endAt: null, rawDurationLabel: "—" };

    const dates = sessionData
      .map((e) => safeDate(e?.sk))
      .filter(Boolean)
      .sort((a, b) => a.getTime() - b.getTime());

    const start = dates[0] || null;
    const end = dates[dates.length - 1] || null;
    const seconds = secondsBetween(start, end);

    return { startAt: start, endAt: end, rawDurationLabel: formatDuration(seconds) };
  }, [hasData, sessionData]);

  const headerTimeLabel = useMemo(() => {
    return formatDateRange(startAt, endAt);
  }, [endAt, startAt]);

  const headerDurationLabel = useMemo(() => rawDurationLabel, [rawDurationLabel]);

  // Auto-enable live polling only if the session's last event was within the past 5 minutes.
  // Uses endAt (the same value shown in the header) so there's no separate timestamp computation.
  // Schedules a timer to turn live OFF exactly when the 5-minute window expires, so polling
  // doesn't continue indefinitely if no new events arrive after the initial check.
  useEffect(() => {
    const manualOverride = manualPollingOverrideRef.current;
    if (manualOverride.sessionKey === sessionKey && manualOverride.enabled) return;

    if (endAt == null) {
      setIsPolling(false);
      return;
    }

    const elapsed = Date.now() - endAt.getTime();
    const shouldLive = elapsed <= LIVE_ACTIVITY_WINDOW_MS;
    setIsPolling((prev) => (prev === shouldLive ? prev : shouldLive));

    if (shouldLive) {
      const remainingMs = LIVE_ACTIVITY_WINDOW_MS - elapsed;
      const timer = setTimeout(() => {
        const override = manualPollingOverrideRef.current;
        if (override.sessionKey === sessionKey && override.enabled) return;
        setIsPolling(false);
      }, remainingMs);
      return () => clearTimeout(timer);
    }
  }, [endAt, sessionKey]);


  const sessionKpis = useMemo(
    () => [
      {
        key: "environments",
        label: "Environments Visited",
        shortLabel: "Environments",
        value: environmentsVisited,
      },
      {
        key: "descriptions",
        label: "Descriptions Read",
        shortLabel: "Descriptions",
        value: descriptionsRead,
      },
      {
        key: "insights",
        label: "Insights Completed",
        shortLabel: "Insights",
        value: insightsCompleted,
      },
    ],
    [descriptionsRead, environmentsVisited, insightsCompleted]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const updateStickyKpis = () => {
      const kpiSection = kpiSectionRef.current;
      if (!kpiSection || !hasData) {
        setShowStickyKpis(false);
        return;
      }

      const { bottom } = kpiSection.getBoundingClientRect();
      const nextVisible = bottom <= getStickyHeaderOffset() + 12;
      setShowStickyKpis((previous) => (previous === nextVisible ? previous : nextVisible));
    };

    const queueStickyMeasure = () => {
      if (stickyMeasureFrameRef.current) return;

      stickyMeasureFrameRef.current = window.requestAnimationFrame(() => {
        stickyMeasureFrameRef.current = 0;
        updateStickyKpis();
      });
    };

    queueStickyMeasure();
    window.addEventListener("scroll", queueStickyMeasure, { passive: true });
    window.addEventListener("resize", queueStickyMeasure);

    return () => {
      if (stickyMeasureFrameRef.current) {
        window.cancelAnimationFrame(stickyMeasureFrameRef.current);
        stickyMeasureFrameRef.current = 0;
      }

      window.removeEventListener("scroll", queueStickyMeasure);
      window.removeEventListener("resize", queueStickyMeasure);
    };
  }, [hasData]);

  // Filter events that should render
  const timelineEvents = useMemo(() => {
    if (!hasData) return [];
    return sessionData.filter(shouldRenderInTimeline);
  }, [hasData, sessionData]);

  useEffect(() => {
    if (!isPolling || timelineEvents.length === 0) return;
    requestAnimationFrame(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    });
  }, [isPolling, timelineEvents.length]);

  return (
    <div className="sessionPage">
      {!dashboard && (
        <Header>
          <PricingLink />
          <ContactLink />
          <SessionCodeInput />
        </Header>
      )}

      <div className="sessionWrap">
        {/* Header Section */}
        <div className="sessionHeader">
          <div className="sessionHeaderLeft">
            <h1 className="sessionTitle">{experience || "Unknown Experience"}</h1>
            <div className="sessionMeta">
              <span className="sessionCode">{rawDisplay || "—"}</span>
              <span className="sessionMetaDot">·</span>
              <span className="sessionDateRange">{headerTimeLabel}</span>
            </div>
          </div>

          <div className="sessionHeaderRight">
            <div className="sessionDurationPill">{headerDurationLabel}</div>
            <AnalyticsHeader
              experience={experience}
              isPolling={isPolling}
              setIsPolling={handlePollingToggle}
            />
          </div>
        </div>

        {notFound && (
          <div className="sessionNotice">
            Session not found (identifier: <b>{rawDisplay || "—"}</b>).
          </div>
        )}

        {fetchError && !notFound && (
          <div className="sessionError">Couldn't load this session.</div>
        )}

        <div
          className={`sessionStickyKpis ${showStickyKpis ? "is-visible" : ""}`}
          aria-hidden="true"
        >
          <div className="sessionStickyKpisInner">
            <div className="sessionStickyKpisList">
              {sessionKpis.map((item) => (
                <div key={item.key} className="sessionStickyKpi">
                  <span className="sessionStickyKpiValue">{item.value}</span>
                  <span className="sessionStickyKpiText">{item.shortLabel}</span>
                </div>
              ))}
            </div>
            <div className="sessionStickyLive">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={isPolling}
                  onChange={() => handlePollingToggle((prev) => !prev)}
                />
                <span className="slider round"></span>
              </label>
              <span className="sessionStickyLiveLabel">Live</span>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div ref={kpiSectionRef} className="sessionKpis">
          {sessionKpis.map((item) => (
            <div key={item.key} className="kpiCard">
              <div className="kpiValue">{item.value}</div>
              <div className="kpiLabel">{item.label}</div>
            </div>
          ))}
        </div>

        {/* Timeline */}
        <div className="sessionTimeline">
          {timelineEvents.length === 0 && hasData && (
            <div className="sessionTimelineEmpty">No timeline events to display.</div>
          )}

          {timelineEvents.map((ev, idx) => (
            <TimelineEvent key={`${ev?.sk || "sk"}-${ev?.pk || "pk"}-${idx}`} data={ev} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Timeline Event Component
const TimelineEvent = memo(function TimelineEvent({ data }) {
  const type = getEventType(data?.pk);
  const { getEnvironmentSync, getItem } = useContent();
  const { environmentId: visitedEnvironmentId, environmentName: visitedEnvironmentName } =
    useMemo(() => getEnvironmentEventDetails(data), [data]);
  const expandedElementId = useMemo(() => getElementEventId(data), [data]);
  const [subtitle, setSubtitle] = useState("");
  const [thumb, setThumb] = useState(null);
  const [thumbError, setThumbError] = useState(false);
  const title =
    type === "EnvironmentVisited"
      ? "Visited Environment"
      : type === "ElementExpanded"
        ? "Read Description"
        : type === "Assessment"
          ? "Insight Completed"
          : "";

  useEffect(() => {
    if (type !== "EnvironmentVisited" || !visitedEnvironmentId) return;

    const env = getEnvironmentSync(visitedEnvironmentId);
    const cachedEnvironmentName = getNamedItemLabel(env);
    if (cachedEnvironmentName) {
      setSubtitle((previous) =>
        previous === cachedEnvironmentName ? previous : cachedEnvironmentName
      );
    }
  }, [type, getEnvironmentSync, visitedEnvironmentId]);

  useEffect(() => {
    let cancelled = false;

    async function hydrateThumb() {
      const targetId =
        type === "EnvironmentVisited"
          ? visitedEnvironmentId
          : type === "ElementExpanded"
            ? expandedElementId
            : "";

      if (!targetId) return;

      const nextThumb = await getThumbnail(targetId);
      if (!cancelled && nextThumb) {
        setThumb((previous) => (previous === nextThumb ? previous : nextThumb));
      }
    }

    hydrateThumb();
    return () => {
      cancelled = true;
    };
  }, [type, visitedEnvironmentId, expandedElementId]);

  useEffect(() => {
    let cancelled = false;

    async function hydrateSubtitle() {
      if (type === "EnvironmentVisited") {
        const env = getEnvironmentSync(visitedEnvironmentId);
        const cachedEnvironmentName = getNamedItemLabel(env);
        if (cachedEnvironmentName) {
          setSubtitle((previous) =>
            previous === cachedEnvironmentName ? previous : cachedEnvironmentName
          );
          return;
        }

        if (visitedEnvironmentId) {
          try {
            const res = await getItem("environments", visitedEnvironmentId);
            const environmentRecord = res?.data ?? res;
            const name =
              getNamedItemLabel(environmentRecord) ||
              visitedEnvironmentName ||
              "Unknown environment";
            if (!cancelled) {
              setSubtitle((previous) => (previous === name ? previous : name));
            }
          } catch {
            const fallbackName = visitedEnvironmentName || "Unknown environment";
            if (!cancelled) {
              setSubtitle((previous) =>
                previous === fallbackName ? previous : fallbackName
              );
            }
          }
        } else {
          const fallbackName = visitedEnvironmentName || "Unknown environment";
          if (!cancelled) {
            setSubtitle((previous) => (previous === fallbackName ? previous : fallbackName));
          }
        }
        return;
      }

      if (type === "ElementExpanded") {
        if (!expandedElementId) {
          setSubtitle((previous) =>
            previous === "Element: Unknown element" ? previous : "Element: Unknown element"
          );
          return;
        }

        try {
          const res = await getItem("elements", expandedElementId);
          const elementRecord = res?.data ?? res;
          const rawName = safeString(
            elementRecord?.name || elementRecord?.labelData?.displayName || res?.name
          );
          const cleaned = rawName ? rawName.replaceAll("_", " ") : "Unknown element";
          const nextSubtitle = `Element: ${cleaned}`;
          if (!cancelled) {
            setSubtitle((previous) => (previous === nextSubtitle ? previous : nextSubtitle));
          }
        } catch {
          if (!cancelled) {
            setSubtitle((previous) =>
              previous === "Element: Unknown element" ? previous : "Element: Unknown element"
            );
          }
        }
      }
    }

    hydrateSubtitle();
    return () => {
      cancelled = true;
    };
  }, [
    type,
    expandedElementId,
    getEnvironmentSync,
    getItem,
    visitedEnvironmentId,
    visitedEnvironmentName,
  ]);

  useEffect(() => {
    setThumbError(false);
  }, [type, visitedEnvironmentId, expandedElementId]);

  const isAssessment = type === "Assessment";
  const placeholder = getPlaceholder(visitedEnvironmentId || expandedElementId || data?.value);

  const handleImageError = () => {
    setThumbError(true);
  };

  if (isAssessment) {
    return <AssessmentEvent data={data} />;
  }

  return (
    <div className="tlRow">
      <div className="tlTime">{formatClock(data?.sk)}</div>

      <div className="tlIconCol">
        <div className="tlIcon">
          {thumb && !thumbError ? (
            <img src={thumb} alt="" onError={handleImageError} />
          ) : (
            <img src={placeholder} alt="" />
          )}
        </div>
        <div className="tlConnector" />
      </div>

      <div className="tlContent">
        <div className="tlTitle">{title}</div>
        <div className="tlSubtitle">{subtitle}</div>
      </div>
    </div>
  );
}, (prevProps, nextProps) => areEventsEquivalent(prevProps.data, nextProps.data));

// Assessment Event Component
function AssessmentEvent({ data }) {
  const parsed = safeJsonParse(data?.value);
  const question = safeString(parsed?.text) || "—";
  const answerRaw = safeString(data?.transcript).trim();
  const hasAnswer = Boolean(answerRaw);
  const feedback = safeString(data?.student_evaluation).trim();
  const summary = safeString(data?.summary).trim();
  const score = data?.score;
  const topics = splitTopics(data?.topics);
  const numericScore = Number(score);
  const displayScore = hasAnswer && Number.isFinite(numericScore) ? Math.round(numericScore) : null;
  const studentBubbleStyle = useMemo(
    () => getScoreSurfaceStyle(displayScore),
    [displayScore]
  );

  return (
    <div className="tlRow tlRowAssessment">
      <div className="tlTime">{formatClock(data?.sk)}</div>

      <div className="tlIconCol">
        <div className="tlIcon tlIconQuestion">
          <span>❓</span>
        </div>
        <div className="tlConnector" />
      </div>

      <div className="tlAssessmentContent">
        <div className="tlAssessmentTitle">Insight Completed</div>

        <div className="tlAssessmentCard">
          <div className="tlAssessmentLayout">
            <div className="tlAssessmentConversation">
              <div className="tlAssessmentBubble tlAssessmentBubble--question">
                <div className="tlAssessmentBubbleLabel">Original Question</div>
                <div className="tlAssessmentBubbleText">{question}</div>
              </div>

              <div
                className="tlAssessmentBubble tlAssessmentBubble--student"
                style={studentBubbleStyle}
              >
                <div className="tlAssessmentBubbleLabel">Student Answer</div>
                <div className={`tlAssessmentBubbleText ${!hasAnswer ? "isBlank" : ""}`}>
                  {hasAnswer ? answerRaw : "No response provided"}
                </div>
              </div>

              <div className="tlAssessmentBubble tlAssessmentBubble--ai">
                <div className="tlAssessmentBubbleLabel">AI Response</div>
                <div className={`tlAssessmentBubbleText ${!feedback ? "isBlank" : ""}`}>
                  {feedback || "No feedback available"}
                </div>
              </div>
            </div>

            <div className="tlAssessmentSidebar">
              <div className="tlAssessmentScoreCard">
                <div className="tlAssessmentScoreLabel">Score</div>
                <Score score={displayScore} emptyLabel="N/A" />
              </div>

              <div className="tlAssessmentSummaryBlock">
                <div className="tlAssessmentSectionLabel">Summary</div>
                {summary ? (
                  <div className="tlAssessmentSummaryText">{summary}</div>
                ) : (
                  <div className="tlAssessmentEmpty">No summary available</div>
                )}
              </div>

              {topics.length > 0 && (
                <div className="tlAssessmentTopicsBlock">
                  <div className="tlAssessmentSectionLabel">Topics</div>
                  <div className="tlAssessmentTopics">
                    {topics.map((topic) => (
                      <span key={topic} className="tlAssessmentTopic">
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
